import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Combobox, Transition } from '@headlessui/react';
import { Search, ArrowRight } from 'lucide-react';
import { useUserMode } from '../../hooks/usePreferences';
import { getPersonaConfig } from '../../utils/personaConfig';
import { cn } from '../../lib/utils';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { usePreferences } from '../../contexts/PreferencesContext';
import { effectiveLens, type PracticeArea } from '../../types/practiceArea';
import { navItemVisible, readActiveRole } from '../common/navGating';
import { useNavFlags } from '../common/navFlags';

const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const mode = useUserMode();
  const config = getPersonaConfig(mode);
  const { activePracticeArea } = usePreferences();
  const reduxUser = useSelector((state: RootState) => state.auth.user);
  const navFlags = useNavFlags();
  // Same lens + role derivation as the Sidebar, and the SAME predicate
  // (navItemVisible) — the palette used to list civil tools in the criminal
  // workspace, which then silently bounced to the dashboard (QA Q29). The
  // role is re-read each time the palette opens (it is per-tab session state).
  const lens = effectiveLens(
    activePracticeArea,
    (reduxUser?.primary_practice_area as PracticeArea | null) ?? null,
  );

  const allPages = useMemo(() => {
    const role = readActiveRole();
    const seen = new Set<string>();
    const pages = [...config.navItems, ...config.toolItems].filter((item) => {
      if (!navItemVisible(item, lens, role, navFlags) || seen.has(item.path)) return false;
      seen.add(item.path);
      return true;
    });
    return pages.map(item => ({
      id: item.path,
      name: item.label,
      icon: item.icon,
      path: item.path,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, lens, open, navFlags]);

  const filtered = query === ''
    ? allPages
    : allPages.filter(page =>
        page.name.toLowerCase().includes(query.toLowerCase())
      );

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    document.addEventListener('open-command-palette', handleOpen);

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('open-command-palette', handleOpen);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSelect = (path: string | null) => {
    if (path) {
      navigate(path);
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[60]" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-navy-950/55 backdrop-blur-md" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-xl transform rounded-xl bg-surface-elevated shadow-dark-elevation-4 border border-border-primary overflow-hidden transition-all">
              <Dialog.Title className="sr-only">Search iAdvocate pages and actions</Dialog.Title>
              <Combobox onChange={handleSelect}>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-text-secondary" />
                  <Combobox.Input
                    className="h-12 w-full bg-transparent border-0 pl-12 pr-4 text-text-primary placeholder:text-text-tertiary focus:ring-0 text-sm"
                    placeholder="Search pages, actions, or ask a question..."
                    onChange={(e) => setQuery(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                {filtered.length > 0 && (
                  <Combobox.Options static className="max-h-72 overflow-y-auto border-t border-border-primary py-2 px-2">
                    {filtered.map((page) => {
                      const Icon = page.icon;
                      return (
                        <Combobox.Option
                          key={page.id}
                          value={page.path}
                          className={({ active }) => cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors',
                            active ? 'bg-legal-green-50 dark:bg-legal-green-900/30' : ''
                          )}
                        >
                          {({ active }) => (
                            <>
                              <Icon className={cn('h-4.5 w-4.5 shrink-0', active ? 'text-legal-green-600 dark:text-legal-green-300' : 'text-text-secondary')} />
                              <span className="text-sm font-medium flex-1 text-text-primary">
                                {page.name}
                              </span>
                              {active && <ArrowRight className="h-4 w-4 text-legal-green-600 dark:text-legal-green-300" />}
                            </>
                          )}
                        </Combobox.Option>
                      );
                    })}
                  </Combobox.Options>
                )}

                {query !== '' && filtered.length === 0 && (
                  <div className="border-t border-border-primary py-8 px-6 text-center">
                    <p className="text-sm text-text-secondary">No pages found.</p>
                  </div>
                )}

                <div className="border-t border-border-primary px-4 py-2.5 flex items-center gap-4 text-[11px] text-text-secondary bg-surface-inset/60">
                  <span><kbd className="font-mono bg-surface-elevated text-text-primary px-1.5 py-0.5 rounded border border-border-primary">Enter</kbd> to go</span>
                  <span><kbd className="font-mono bg-surface-elevated text-text-primary px-1.5 py-0.5 rounded border border-border-primary">Esc</kbd> to close</span>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CommandPalette;
